
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate, useLocation } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
import { FaFacebook, FaApple } from "react-icons/fa";
import { toast } from "sonner";
import { useAuth } from "@/providers/AuthProvider";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2, Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface LocationState {
  from?: {
    pathname: string;
  };
}

const SignIn = () => {
  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // Loading state
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  
  // Error handling
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Navigation and auth
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  
  // Get redirect path or default to dashboard
  const from = (location.state as LocationState)?.from?.pathname || "/dashboard";

  // Check if user is already logged in
  useEffect(() => {
    if (user && !authLoading) {
      console.log("[SignIn] User is already authenticated, redirecting to:", from);
      navigate(from, { replace: true });
    }
  }, [user, authLoading, navigate, from]);

  // Email/password sign in handler
  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setLoading(true);
    
    try {
      console.log("[SignIn] Attempting email sign in with:", email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        console.error("[SignIn] Sign in error:", error);
        setErrorMessage(error.message);
        toast.error("Sign-in failed: " + error.message);
        return;
      } 
      
      if (data.user) {
        console.log("[SignIn] Sign in successful - user ID:", data.user.id);
        toast.success("Sign in successful!");
        
        // Redirect to the intended page
        navigate(from, { replace: true });
      } else {
        console.error("[SignIn] Sign in response has no user");
        setErrorMessage("Sign in failed. Please try again.");
        toast.error("Sign in failed. Please try again.");
      }
    } catch (error: any) {
      console.error("[SignIn] Sign in exception:", error);
      setErrorMessage(error.message || "Authentication failed. Please try again.");
      toast.error("Authentication error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Social sign in handler
  const handleSocialSignIn = async (provider: "google" | "facebook" | "apple") => {
    setSocialLoading(provider);
    setErrorMessage(null);
    
    try {
      console.log(`[SignIn] Starting ${provider} sign in flow`);
      
      const redirectTo = `${window.location.origin}/dashboard`;
      console.log(`[SignIn] Redirect URL: ${redirectTo}`);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo,
          // Add query params for Google to get refresh token
          queryParams: provider === 'google' ? {
            access_type: 'offline',
            prompt: 'consent',
          } : undefined
        }
      });
      
      if (error) {
        console.error(`[SignIn] ${provider} sign in error:`, error);
        setErrorMessage(error.message);
        toast.error(`${provider} sign-in failed: ` + error.message);
      } else if (data?.url) {
        console.log(`[SignIn] ${provider} redirect URL:`, data.url);
        // No need to redirect manually - Supabase will handle it
      } else {
        console.error(`[SignIn] No redirect URL returned from ${provider} auth`);
        setErrorMessage(`${provider} authentication failed. Please try again.`);
        toast.error(`${provider} authentication failed. Please try again.`);
      }
    } catch (error: any) {
      console.error(`[SignIn] ${provider} sign in exception:`, error);
      setErrorMessage(error.message || "Authentication failed. Please try again.");
      toast.error("Authentication error. Please try again.");
    } finally {
      setSocialLoading(null);
    }
  };

  // Show loading spinner during auth state transition
  if (authLoading && user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-heaven-gold mb-4" />
        <p className="text-lg text-gray-600">Signing you in...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-12 flex flex-col items-center">
      <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-xl shadow-lg border border-heaven-gold/20">
        <div className="text-center">
          <h1 className="text-3xl font-playfair font-bold text-gray-800">Sign In</h1>
          <p className="mt-2 text-gray-600">Welcome back to Selah</p>
        </div>

        {errorMessage && (
          <Alert variant="destructive" className="border-red-500 text-red-700 bg-red-50">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <Button
            onClick={() => handleSocialSignIn("google")}
            variant="outline"
            className="w-full flex items-center justify-center gap-2 py-6"
            disabled={!!socialLoading}
          >
            {socialLoading === "google" ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Connecting...</span>
              </>
            ) : (
              <>
                <FcGoogle className="h-5 w-5" />
                <span>Continue with Google</span>
              </>
            )}
          </Button>
          
          <Button
            onClick={() => handleSocialSignIn("facebook")}
            variant="outline"
            className="w-full flex items-center justify-center gap-2 py-6 bg-[#1877F2] text-white hover:bg-[#0d65d9]"
            disabled={!!socialLoading}
          >
            {socialLoading === "facebook" ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Connecting...</span>
              </>
            ) : (
              <>
                <FaFacebook className="h-5 w-5" />
                <span>Continue with Facebook</span>
              </>
            )}
          </Button>
          
          <Button
            onClick={() => handleSocialSignIn("apple")}
            variant="outline"
            className="w-full flex items-center justify-center gap-2 py-6 bg-black text-white hover:bg-gray-800"
            disabled={!!socialLoading}
          >
            {socialLoading === "apple" ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Connecting...</span>
              </>
            ) : (
              <>
                <FaApple className="h-5 w-5" />
                <span>Continue with Apple</span>
              </>
            )}
          </Button>
          
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with email</span>
            </div>
          </div>
          
          <form onSubmit={handleEmailSignIn} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <a
                  href="/reset-password"
                  className="text-sm font-medium text-heaven-gold hover:text-heaven-dark-gold"
                >
                  Forgot password?
                </a>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <Button
              type="submit"
              variant="gold"
              className="w-full py-6"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>
        </div>
        
        <div className="text-center mt-4">
          <p className="text-sm text-gray-600">
            Don't have an account?{" "}
            <a
              href="/sign-up"
              className="font-medium text-heaven-gold hover:text-heaven-dark-gold"
            >
              Sign up
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
